import { createReward, myRewards, updateReward } from "@/graphql/mutations";
import { getUsersPointsProgress, rewardUsersByUserID } from "@/graphql/queries";
import { useMutation, useQuery } from "@tanstack/react-query";
import customGraphQL from "./useGraphQLWithSentryWrapper";
import { API, graphqlOperation } from "aws-amplify";

import { GraphQLQuery } from "@aws-amplify/api";

export interface IListReward {
  myRewards: IReward[];
}

export interface IReward {
  description: string;
  imageUrl: string;
  isActive: boolean;
  points: number | string;
  organizationGroup: string;
  pointsDiscount: number;
  title: string;
  id: string;
  updatedAt: string; // Assuming this is a date string
  _deleted: boolean;
  _lastChangedAt: string; // Assuming this is a date string
  _version: number;
  createdAt: string; // Assuming this is a date string
}

export interface IReedemedRewards {
  qty: number;
  id: string;
  reward: {
    id: string;
    organizationGroup: string;
    title: string;
    description: string;
    isActive: boolean;
    points: string | number;
    pointsDiscount: string | number;
    imageUrl: string;
    createdAt: string;
    updatedAt: string;
  };
  rewardID: string;
  userID: string;
  user: {
    id: string;
    cognitoID: string;
    email: string;
    organizationGroup: string;
    firstName: string;
    lastName: string;
    username: string;
    avatarUrl: string;
    bannerImageUrl: string;
    bio: string;
    jobTitle: string;
    type: string;
    lookingForType: string;
    joinedAt: string;
    phone: string;
    points: string | number;
    lifetimePoints: string | number;
    dateOfBirth: string;
    isOnboarded: boolean;

    department: {
      id: string;
      name: string;
      description: string;
      organizationGroup: string;
      createdAt: string;
      updatedAt: string;
    };
  };
}

type PurposeStrings = "LIST" | "REDEEM";

interface PointsBreakdownItem {
  key: string;
  value: string;
}

interface PointsTier {
  key: string;
  value: string;
}

interface RewardsProgress {
  percentToNextTier: number;
  sinceLastWeek: number;
  pointsBalance: number;
}

interface IFetchUserPointProgress {
  getUsersPointsProgress: {
    nextTier: string;
    pointsBreakdown: PointsBreakdownItem[];
    pointsTiers: PointsTier[];
    pointsToNextTier: number;
    rewardsProgress: RewardsProgress;
    statusCode: number;
  };
}

interface ListRewardInput {
  purpose: PurposeStrings;
  rewardID?: string;
}

async function fetchUserPointProgress() {
  const response = await customGraphQL<IFetchUserPointProgress>(
    getUsersPointsProgress
  );
  const records = response?.getUsersPointsProgress ?? {};
  return records;
}

export const useMetricsRewards = () => {
  return useQuery({
    // @ts-ignore
    queryKey: [`KEY_GET_METRICS_REWARD`], // Corrected queryKey
    queryFn: () => fetchUserPointProgress(),
  });
};

async function fetchListReward(rewardData: ListRewardInput) {
  const response = await customGraphQL<IListReward>(myRewards, rewardData);
  const records = response?.myRewards ?? [];
  return records;
}

export const useRedeemReward = () => {
  return useMutation(fetchListReward);
};

export const useListReward = (rewardData: ListRewardInput) => {
  return useQuery<IReward[], Error>({
    queryKey: [`KEY_GET_${rewardData.purpose}_REWARD`],
    queryFn: () => {
      if (rewardData?.purpose === "REDEEM" && !rewardData?.rewardID)
        return [] as IReward[];
      return fetchListReward(rewardData);
    },
  });
};

export const useListRedeemedRewards = (userID: string) => {
  return useQuery<IReedemedRewards[], Error>({
    queryKey: [`KEY_GET_REDEEMED_REWARD`],
    queryFn: async () => {
      try {
        const responseRaw: any = await API.graphql(
          graphqlOperation(rewardUsersByUserID, {
            userID,
          })
        );

        const response: IReedemedRewards[] =
          responseRaw?.data?.rewardUsersByUserID?.items;

        if (!response) return [];
        const seen = new Map();
        response.forEach((item) => {
          const value = item["rewardID"];
          if (!seen.has(value)) {
            seen.set(value, { ...item, qty: 1 });
          } else {
            const existingItem = seen.get(value);
            existingItem.qty++;
            seen.set(value, existingItem);
          }
        });

        const uniqueFiltered = Array.from(seen.values());
        return uniqueFiltered;
      } catch (error: any) {
        throw error;
      }
    },
    enabled: false,
  });
};

interface ICreateRewardPayload {
  _version?: number;
  description: string;
  id?: string;
  imageUrl: string;
  isActive?: boolean;
  organizationGroup: string;
  points: number;
  pointsDiscount?: number;
  title: string;
}

export const createRewards = async (payload: ICreateRewardPayload) => {
  return await API.graphql<GraphQLQuery<any>>({
    query: payload.id ? updateReward : createReward,
    variables: {
      input: payload,
    },
  });
};
